import React from 'react';
import { UserCircle, ShieldCheck, Star, Check, Clock, AlertTriangle, ChevronRight, FileText, Gift, Share2, Copy, Camera } from 'lucide-react';
import { auth } from '../firebase';
import { useAppContext } from '../context/AppContext';
import { v2, getWorkerReviewStatus, getWorkerReviewTone } from '../constants';

export default function ProfileView() {
  const {
    lang, t, user, userData, role, orders, isAdmin,
    setView, setFilterStatus, handleResubmitWorkerProfile, handleShare, showToast
  } = useAppContext();

  const workerReviewStatusText = {
    APPROVED: lang === 'es' ? 'Aprobado' : '已通过',
    PENDING: lang === 'es' ? 'En revisión' : '审核中',
    REJECTED: lang === 'es' ? 'Rechazado' : '已驳回',
    INCOMPLETE: lang === 'es' ? 'Incompleto' : '未完善'
  };

  const workerReviewStatus = role === 'worker' ? getWorkerReviewStatus(userData || {}) : null;
  const workerReviewTone = workerReviewStatus ? getWorkerReviewTone(workerReviewStatus) : { bg: '#F8FAFC', color: v2.colors.muted, border: '#E3E8EF' };

  const workerHasDocs = Boolean(userData?.idPhoto && userData?.selfie);
  const workerHasSkills = Boolean(userData?.skills && userData.skills.length > 0);
  const workerHasBank = Boolean(userData?.bankInfo?.accName && userData?.bankInfo?.accNum && userData?.bankInfo?.accType);
  const workerHasInvoiceTerms = Boolean(userData?.invoice_terms_accepted);
  const workerCanResubmit = Boolean(workerHasDocs && workerHasSkills && workerHasBank && workerHasInvoiceTerms && userData?.firstName && userData?.lastName && userData?.phone && userData?.address);
  const workerCanReceiveOrders = role !== 'worker' || (userData?.isVerified && workerHasSkills);

  let myAvgRating = '5.0';
  let myTotalReviews = 0;
  if (role === 'worker' && user) {
    const myReviews = orders.filter(o => o.techId === user.uid && o.status === 'COMPLETED' && o.rating);
    if (myReviews.length > 0) {
      myTotalReviews = myReviews.length;
      myAvgRating = (myReviews.reduce((acc, curr) => acc + curr.rating, 0) / myTotalReviews).toFixed(1);
    }
  }

  const profilePendingCount = role === 'worker'
    ? orders.filter(o => o.status === 'OPEN').length
    : orders.filter(o => ['PENDING', 'PENDING_PAYMENT', 'AWAITING_VERIFICATION'].includes(o.status)).length;
  const profileActiveCount = orders.filter(o => ['ACCEPTED', 'ASSIGNED', 'WORKING', 'PENDING_APPROVAL'].includes(o.status)).length;
  const profileDoneCount = orders.filter(o => o.status === 'COMPLETED').length;

  const P = '#2962FF';
  const heroGrad = 'linear-gradient(135deg, #1648D8 0%, #2962FF 55%, #1A3FBB 100%)';

  return (
    <div style={{ background: '#F5F7FA', minHeight: '100vh', padding: '14px 16px 24px' }}>
      {/* 用户头像 & 名称 */}
      <div style={{ background: heroGrad, padding: '20px', borderRadius: '20px', color: '#fff', marginBottom: '14px', boxShadow: '0 8px 24px rgba(41,98,255,0.28)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{ position: 'relative', flexShrink: 0 }}>
            {userData?.avatar ? (
              <img src={userData.avatar}
                style={{ width: '62px', height: '62px', borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.50)', display: 'block' }}
                alt="avatar" />
            ) : (
              <div style={{ width: '62px', height: '62px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.20)', border: '2px solid rgba(255,255,255,0.40)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <UserCircle size={38} color="#fff" />
              </div>
            )}
            {/* 相机编辑按钮 */}
            <div onClick={() => setView('editProfile')}
              style={{ position: 'absolute', bottom: '0', right: '0', width: '22px', height: '22px', borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.18)' }}>
              <Camera size={12} color="#2962FF" />
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: 'rgba(255,255,255,0.75)', fontWeight: '700' }}>
              {role === 'worker' ? (lang === 'es' ? 'Cuenta de técnico' : '师傅账户') : (lang === 'es' ? 'Cuenta de cliente' : '客户账户')}
            </p>
            <h2 style={{ margin: 0, fontSize: '20px', color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {userData?.name || userData?.display_id || 'FixBox'}
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '9px', flexWrap: 'wrap' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', backgroundColor: 'rgba(255,255,255,0.20)', color: '#fff', padding: '5px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: '800' }}>
                <ShieldCheck size={13} /> {role === 'worker' ? workerReviewStatusText[workerReviewStatus] : t.verified}
              </div>
              {role === 'worker' && userData?.isVerified && (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', backgroundColor: 'rgba(255,255,255,0.12)', color: '#fff', padding: '5px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: '800' }}>
                  <Star size={13} fill="#D4AF37" color="#D4AF37" /> {myAvgRating} ({myTotalReviews})
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 师傅：接单资格状态 */}
      {role === 'worker' && (
        <div style={{ ...v2.card, padding: '16px', marginBottom: '14px', border: workerCanReceiveOrders ? '1px solid #BBF7D0' : v2.card.border }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div>
              <p style={{ margin: '0 0 4px 0', color: v2.colors.muted, fontSize: '12px', fontWeight: '700' }}>
                {lang === 'es' ? 'Estado para tomar pedidos' : '接单资格'}
              </p>
              <b style={{ fontSize: '16px', color: workerCanReceiveOrders ? v2.colors.green : workerReviewTone.color }}>
                {workerCanReceiveOrders
                  ? (lang === 'es' ? 'Listo para recibir trabajos' : '已可正常接单')
                  : workerReviewStatusText[workerReviewStatus]}
              </b>
              {workerReviewStatus === 'REJECTED' && (
                <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#B91C1C', lineHeight: '1.4', fontWeight: '700' }}>
                  {userData?.verification_rejection}
                </p>
              )}
            </div>
            {!workerCanReceiveOrders && (
              <button onClick={() => setView('editProfile')} style={{ padding: '9px 10px', border: 'none', borderRadius: '8px', background: P, color: '#fff', fontSize: '12px', fontWeight: '800', cursor: 'pointer' }}>
                {lang === 'es' ? 'Completar' : '去完善'}
              </button>
            )}
          </div>

          {/* 资质清单 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(72px, 1fr))', gap: '8px' }}>
            {[
              { ok: workerHasDocs, labelEs: 'Documentos', labelZh: '证件资料' },
              { ok: workerHasSkills, labelEs: 'Servicios', labelZh: '服务类型' },
              { ok: workerHasBank, labelEs: 'Cobro', labelZh: '收款' },
              { ok: workerHasInvoiceTerms, labelEs: 'Factura', labelZh: '发票' },
              { ok: userData?.isVerified, labelEs: 'Aprobación', labelZh: '平台审核', pendingColor: '#B45309', pendingBorder: '#FCD34D', pendingBg: '#FFF7E6' }
            ].map((item, i) => (
              <div key={i} style={{
                background: item.ok ? v2.colors.softGreen : (item.pendingBg || '#F8FAFC'),
                border: `1px solid ${item.ok ? '#BBF7D0' : (item.pendingBorder || '#E3E8EF')}`,
                borderRadius: '8px', padding: '10px'
              }}>
                {item.ok
                  ? <Check size={16} color={v2.colors.green} />
                  : <Clock size={16} color={item.pendingColor || '#94A3B8'} />}
                <p style={{ margin: '6px 0 0 0', fontSize: '11px', color: v2.colors.ink, fontWeight: '800' }}>
                  {lang === 'es' ? item.labelEs : item.labelZh}
                </p>
              </div>
            ))}
          </div>

          {/* 被驳回时的重新提交区域 */}
          {workerReviewStatus === 'REJECTED' && (
            <div style={{ marginTop: '12px', background: v2.colors.softRed, border: '1px solid #FECACA', borderRadius: '8px', padding: '12px' }}>
              <p style={{ margin: '0 0 10px 0', color: '#7F1D1D', fontSize: '12px', lineHeight: '1.45', fontWeight: '800' }}>
                {workerCanResubmit
                  ? (lang === 'es' ? 'Si ya corregiste la información, envíala de nuevo para que el administrador la revise.' : '如果资料已经改好，请点击重新提交，让后台再次审核。')
                  : (lang === 'es' ? 'Corrige los datos marcados y guarda desde Mis Datos para reenviar la revisión.' : '请先进入"我的资料"修正缺失内容并保存，才能重新提交审核。')}
              </p>
              <button
                disabled={!workerCanResubmit}
                onClick={handleResubmitWorkerProfile}
                style={{ width: '100%', padding: '12px', borderRadius: '8px', border: 'none', background: workerCanResubmit ? '#B91C1C' : '#E5E7EB', color: workerCanResubmit ? '#fff' : '#94A3B8', fontWeight: '900', cursor: workerCanResubmit ? 'pointer' : 'not-allowed' }}>
                {lang === 'es' ? 'Reenviar a revisión' : '重新提交审核'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* 余额 & 订单统计 */}
      <div style={{ ...v2.card, padding: '16px', marginBottom: '14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <div>
            <p style={{ margin: '0 0 4px 0', color: v2.colors.muted, fontSize: '12px', fontWeight: '700' }}>{t.balance}</p>
            <h2 style={{ margin: 0, fontSize: '28px', color: v2.colors.green }}>${(userData?.wallet_balance || 0).toFixed(2)}</h2>
          </div>
          <button onClick={() => setView('wallet')} style={{ padding: '10px 12px', borderRadius: '10px', border: 'none', background: P, color: '#fff', fontWeight: '800', cursor: 'pointer' }}>
            {t.wallet}
          </button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
          <div onClick={() => { setFilterStatus(role === 'worker' ? 'OPEN' : 'PENDING'); setView('history'); }}
            style={{ background: v2.colors.softBlue, border: '1px solid #BFDBFE', borderRadius: '8px', padding: '10px', cursor: 'pointer' }}>
            <b style={{ fontSize: '20px', color: '#1D4ED8' }}>{profilePendingCount}</b>
            <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#1E3A8A', fontWeight: '800' }}>{role === 'worker' ? t.w_p1 : t.c_p1}</p>
          </div>
          <div onClick={() => { setFilterStatus('ACTIVE'); setView('history'); }}
            style={{ background: v2.colors.softGold, border: '1px solid #FCD34D', borderRadius: '8px', padding: '10px', cursor: 'pointer' }}>
            <b style={{ fontSize: '20px', color: '#B45309' }}>{profileActiveCount}</b>
            <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#92400E', fontWeight: '800' }}>{role === 'worker' ? t.w_p2 : t.c_p2}</p>
          </div>
          <div onClick={() => { setFilterStatus('COMPLETED'); setView('history'); }}
            style={{ background: v2.colors.softGreen, border: '1px solid #BBF7D0', borderRadius: '8px', padding: '10px', cursor: 'pointer' }}>
            <b style={{ fontSize: '20px', color: '#15803D' }}>{profileDoneCount}</b>
            <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#166534', fontWeight: '800' }}>{role === 'worker' ? t.w_p3 : t.c_p3}</p>
          </div>
        </div>
      </div>

      {/* 导航菜单 */}
      <div style={{ ...v2.card, overflow: 'hidden', marginBottom: '14px' }}>
        <div onClick={() => setView('editProfile')}
          style={{ padding: '16px', borderBottom: '1px solid #EEF2F6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', fontSize: '15px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <UserCircle size={20} color={v2.colors.ink} />
            <b>{t.edit_profile}</b>
          </div>
          {role === 'worker' && (!userData?.isVerified || !userData?.skills || userData.skills.length === 0) ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: '5px', color: workerReviewTone.color, fontSize: '12px', fontWeight: '800' }}>
              {workerReviewStatus === 'PENDING' ? <Clock size={14} /> : <AlertTriangle size={14} />}
              {workerReviewStatusText[workerReviewStatus]} <ChevronRight size={16} />
            </span>
          ) : <ChevronRight size={18} color="#94A3B8" />}
        </div>
        <div onClick={() => setView('legal')}
          style={{ padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', fontSize: '15px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FileText size={20} color={v2.colors.ink} />
            <b>{t.rules}</b>
          </div>
          <ChevronRight size={18} color="#94A3B8" />
        </div>
      </div>

      {isAdmin && (
        <button onClick={() => setView('admin')}
          style={{ width: '100%', padding: '15px', background: heroGrad, color: '#fff', borderRadius: '14px', fontWeight: '900', cursor: 'pointer', marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontSize: '16px', border: 'none', boxShadow: '0 4px 14px rgba(41,98,255,0.30)' }}>
          {lang === 'es' ? 'Panel de Control' : '老板总控台'}
        </button>
      )}

      {/* 推荐码卡片 */}
      {userData?.referral_code && (
        <div style={{ ...v2.card, padding: '16px', marginBottom: '12px', background: 'linear-gradient(135deg, #F0FDF4 0%, #ECFDF3 100%)', border: '1px solid #BBF7D0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
            <Gift size={18} color={v2.colors.green} />
            <b style={{ fontSize: '15px', color: v2.colors.ink }}>{t.referral_title}</b>
          </div>
          <p style={{ fontSize: '12px', color: '#3F5F4A', margin: '0 0 12px 0', lineHeight: '1.4', fontWeight: '700' }}>{t.referral_desc}</p>
          {/* 推荐码展示 */}
          <div style={{ background: '#fff', borderRadius: '8px', padding: '10px 14px', marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #D1FAE5' }}>
            <div>
              <p style={{ margin: 0, fontSize: '11px', color: v2.colors.muted, fontWeight: '700' }}>{t.referral_code_label}</p>
              <b style={{ fontSize: '22px', letterSpacing: '4px', color: v2.colors.green }}>{userData.referral_code}</b>
            </div>
            <button onClick={() => { navigator.clipboard.writeText(userData.referral_code); showToast(t.referral_copied, lang === 'es' ? 'Código copiado al portapapeles.' : '推荐码已复制到剪贴板。'); }}
              style={{ padding: '8px', background: '#DCFCE7', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Copy size={16} color={v2.colors.green} />
            </button>
          </div>
          <button onClick={handleShare}
            style={{ width: '100%', padding: '12px', backgroundColor: '#25D366', color: '#fff', borderRadius: '8px', border: 'none', fontWeight: '900', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <Share2 size={16} /> {t.referral_share_btn}
          </button>
        </div>
      )}

      <button onClick={() => auth.signOut()}
        style={{ width: '100%', padding: '15px', color: v2.colors.red, background: '#fff', border: '1px solid #FECACA', borderRadius: '8px', fontWeight: '900', cursor: 'pointer', fontSize: '16px' }}>
        {lang === 'es' ? 'Cerrar Sesión' : '退出登录'}
      </button>
    </div>
  );
}
